import {
  getFieldDef,
  isFieldElement,
  LayoutElement,
} from "@entaconsulting/form";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Grid, styled, Typography } from "@mui/material";
import { useMemo } from "react";
import { FormRenderProps, withTypes } from "react-final-form";
import { useAppSelector } from "store/hooks";
import useColumnEdited from "../hooks/useColumnEdited";
import { EditedElement } from "../model";
import VisualEditorPanelSections from "./dynamicForms/VisualEditorPanelSections";
import resolveLayoutElement from "./jsonSchema/resolveJsonElement";

type IDynamicForm = {
  routeToElement: string;
  onDelete: () => void;
};

const GridStyled = styled(Grid)({
  height: "100%",
});

const GridScrollable = styled(Grid)(({ theme }) => ({
  height: "auto",
  width: "100%",
  maxWidth: "100%",
  padding: theme.spacing(1.5, 0.5),
  overflowY: "scroll",
  overflowX: "hidden",
}));

const DynamicForm = (props: IDynamicForm) => {
  const { routeToElement, onDelete } = props;
  const columnEdited = useColumnEdited();
  const { Form } = withTypes<EditedElement>();

  const { formDefinitionPreview } = useAppSelector(
    (state) => state.developer.edit.editor
  );

  const element: LayoutElement | null = useMemo(() => {
    //It's header
    if (
      routeToElement.startsWith("header") &&
      formDefinitionPreview.layout.header
    ) {
      return resolveLayoutElement(
        formDefinitionPreview.layout.header.elements,
        routeToElement.split("/").slice(1)
      );
    }

    //It's body
    else if (routeToElement.startsWith("body"))
      return resolveLayoutElement(
        formDefinitionPreview.layout.elements,
        routeToElement.split("/").slice(1)
      );
    else return null;
  }, [
    formDefinitionPreview.layout.elements,
    formDefinitionPreview.layout.header,
    routeToElement,
  ]);

  const formElement: EditedElement | null = useMemo(() => {
    if (!element) return null;
    const fieldDef =
      element && isFieldElement(element)
        ? getFieldDef(formDefinitionPreview.schema, element.name)
        : undefined;
    return {
      element,
      fieldDef,
    };
  }, [element, formDefinitionPreview.schema]);

  if (!formElement)
    return (
      <Box
        height="100%"
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Typography variant="caption" align="center">
          Select an element to edit it's properties.
        </Typography>
      </Box>
    );

  return (
    <GridStyled
      container
      direction="column"
      key={columnEdited ? columnEdited.index : routeToElement}
    >
      <GridScrollable item xs>
        <Form onSubmit={() => {}} initialValues={formElement}>
          {(props: FormRenderProps<EditedElement>) => (
            <VisualEditorPanelSections />
          )}
        </Form>
      </GridScrollable>
      <Grid container item justifyContent="flex-end">
        <Grid item>
          <Button
            startIcon={<DeleteIcon />}
            size="small"
            onClick={onDelete}
            color="inherit"
          >
            Delete
          </Button>
        </Grid>
      </Grid>
    </GridStyled>
  );
};

export default DynamicForm;
